import { Link } from 'react-router'
import { Icon } from '#app/components/ui/icon.tsx'
import { type Route } from './+types/privacy.ts'

export const meta: Route.MetaFunction = () => {
	return [
		{ title: 'Privacy Policy - MyBillingsMethod' },
		{
			name: 'description',
			content:
				'MyBillingsMethod privacy policy. Your fertility data never leaves your Mac. We only collect email and payment info. No tracking, no analytics on personal data.',
		},
	]
}

export default function PrivacyRoute() {
	const lastUpdated = 'December 13, 2025'

	return (
		<div className="bg-white">
			{/* Navigation */}
			<nav className="sticky top-0 z-40 border-b border-gray-100 bg-white/80 backdrop-blur-sm">
				<div className="mx-auto max-w-5xl px-6 py-4">
					<div className="flex items-center justify-between">
						<Link to="/" className="flex items-center space-x-2">
							<Icon name="lock-closed" className="h-6 w-6 text-purple-600" />
							<span className="text-lg font-semibold text-gray-900">
								MyBillingsMethod
							</span>
						</Link>
						<Link
							to="/"
							className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-gray-900"
						>
							<Icon name="arrow-left" className="mr-2 h-4 w-4" />
							Back to Home
						</Link>
					</div>
				</div>
			</nav>

			{/* Header */}
			<div className="bg-gradient-to-br from-purple-600 to-purple-800 text-white">
				<div className="mx-auto max-w-4xl px-6 py-16">
					<h1 className="mb-4 text-4xl font-bold">Privacy Policy</h1>
					<p className="text-lg text-purple-100">
						Your data is yours. We mean it.
					</p>
					<p className="mt-4 text-sm text-purple-200">
						Last updated: {lastUpdated}
					</p>
				</div>
			</div>

			{/* Content */}
			<div className="mx-auto max-w-4xl px-6 py-12">
				{/* Key Privacy Promise - Highlighted */}
				<div className="mb-12 rounded-xl border-2 border-green-200 bg-green-50 p-8">
					<h2 className="mb-4 flex items-center text-2xl font-bold text-green-900">
						<Icon name="lock-closed" className="mr-3 h-8 w-8" />
						Our Core Privacy Commitment
					</h2>
					<p className="mb-4 text-lg text-gray-700">
						<strong>Your fertility data never leaves your Mac.</strong>
					</p>
					<p className="text-gray-600">
						MyBillingsMethod stores all fertility observations, cycle data, and
						health information locally on your device. We don't have servers to
						store this data. We can't access it. We can't lose it in a breach.
						This isn't just a promise, it's our architecture.
					</p>
				</div>

				{/* 1. Information We Collect */}
				<section className="mb-12">
					<h2 className="mb-6 text-3xl font-bold text-gray-900">
						1. Information We Collect
					</h2>

					<div className="space-y-6">
						<div>
							<h3 className="mb-3 text-xl font-semibold text-gray-900">
								1.1 Data We DO Collect (Website Only)
							</h3>
							<ul className="space-y-2 text-gray-700">
								<li className="flex items-start">
									<span className="mr-2 text-purple-600">•</span>
									<span>
										<strong>Email address:</strong> To create your account and
										send you important updates
									</span>
								</li>
								<li className="flex items-start">
									<span className="mr-2 text-purple-600">•</span>
									<span>
										<strong>Payment information:</strong> Processed by Stripe (we
										never see your card details)
									</span>
								</li>
								<li className="flex items-start">
									<span className="mr-2 text-purple-600">•</span>
									<span>
										<strong>Subscription details:</strong> Plan type, billing
										dates, payment status
									</span>
								</li>
								<li className="flex items-start">
									<span className="mr-2 text-purple-600">•</span>
									<span>
										<strong>Device information:</strong> Mac OS version (to ensure
										app compatibility)
									</span>
								</li>
							</ul>
						</div>

						<div>
							<h3 className="mb-3 text-xl font-semibold text-gray-900">
								1.2 Data We DO NOT Collect
							</h3>
							<ul className="space-y-2 text-gray-700">
								<li className="flex items-start">
									<span className="mr-2 text-red-600">✗</span>
									<span>
										<strong>Fertility observations:</strong> Stored locally on
										your Mac only
									</span>
								</li>
								<li className="flex items-start">
									<span className="mr-2 text-red-600">✗</span>
									<span>
										<strong>Cycle data:</strong> Stored locally on your Mac only
									</span>
								</li>
								<li className="flex items-start">
									<span className="mr-2 text-red-600">✗</span>
									<span>
										<strong>Health information:</strong> Stored locally on your
										Mac only
									</span>
								</li>
								<li className="flex items-start">
									<span className="mr-2 text-red-600">✗</span>
									<span>
										<strong>Browsing history:</strong> We don't track what you do
										in the app
									</span>
								</li>
								<li className="flex items-start">
									<span className="mr-2 text-red-600">✗</span>
									<span>
										<strong>Location data:</strong> We don't need to know where
										you are
									</span>
								</li>
								<li className="flex items-start">
									<span className="mr-2 text-red-600">✗</span>
									<span>
										<strong>Third-party tracking:</strong> No Google Analytics, no
										Facebook Pixel, no surveillance
									</span>
								</li>
							</ul>
						</div>
					</div>
				</section>

				{/* 2. How We Use Your Information */}
				<section className="mb-12">
					<h2 className="mb-6 text-3xl font-bold text-gray-900">
						2. How We Use Your Information
					</h2>
					<p className="mb-4 text-gray-700">
						We use the limited information we collect to:
					</p>
					<ul className="ml-6 space-y-2 text-gray-700">
						<li className="flex items-start">
							<span className="mr-2 text-purple-600">•</span>
							<span>Create and manage your account</span>
						</li>
						<li className="flex items-start">
							<span className="mr-2 text-purple-600">•</span>
							<span>Process payments and manage subscriptions</span>
						</li>
						<li className="flex items-start">
							<span className="mr-2 text-purple-600">•</span>
							<span>Send you app download links and updates</span>
						</li>
						<li className="flex items-start">
							<span className="mr-2 text-purple-600">•</span>
							<span>Respond to support requests</span>
						</li>
						<li className="flex items-start">
							<span className="mr-2 text-purple-600">•</span>
							<span>
								Send important service announcements (launch dates, security
								updates)
							</span>
						</li>
					</ul>
					<p className="mt-4 text-gray-700">
						<strong>We do NOT:</strong> Sell your data, share it with
						advertisers, or use it for marketing purposes beyond essential service
						communications.
					</p>
				</section>

				{/* 3. Data Storage and Security */}
				<section className="mb-12">
					<h2 className="mb-6 text-3xl font-bold text-gray-900">
						3. Data Storage and Security
					</h2>

					<div className="space-y-4 text-gray-700">
						<div>
							<h3 className="mb-2 text-lg font-semibold text-gray-900">
								Your Fertility Data (App)
							</h3>
							<p>
								All fertility observations, cycle data, and health information are
								stored in an encrypted SQLite database on your Mac. This data
								never syncs to our servers. It never leaves your device. Only you
								have access to it.
							</p>
						</div>

						<div>
							<h3 className="mb-2 text-lg font-semibold text-gray-900">
								Account Data (Website)
							</h3>
							<p>
								Your email and subscription information is stored on secure servers
								(Fly.io) with industry-standard encryption. Payment details are
								handled exclusively by Stripe, we never see or store your card
								information.
							</p>
						</div>

						<div>
							<h3 className="mb-2 text-lg font-semibold text-gray-900">
								Backups
							</h3>
							<p>
								You control your own backups. Use macOS Time Machine or export
								your data to PDF. We don't have your fertility data to back up.
							</p>
						</div>
					</div>
				</section>

				{/* 4. Third-Party Services */}
				<section className="mb-12">
					<h2 className="mb-6 text-3xl font-bold text-gray-900">
						4. Third-Party Services
					</h2>
					<p className="mb-4 text-gray-700">
						We use these third-party services:
					</p>

					<div className="space-y-4">
						<div className="border-l-4 border-purple-600 pl-4">
							<h3 className="font-semibold text-gray-900">
								Stripe (Payment Processing)
							</h3>
							<p className="text-sm text-gray-600">
								Handles all payment processing. View their privacy policy at
								stripe.com/privacy
							</p>
						</div>

						<div className="border-l-4 border-purple-600 pl-4">
							<h3 className="font-semibold text-gray-900">
								Resend (Transactional Emails)
							</h3>
							<p className="text-sm text-gray-600">
								Sends account-related emails only. View their privacy policy at
								resend.com/legal/privacy-policy
							</p>
						</div>

						<div className="border-l-4 border-purple-600 pl-4">
							<h3 className="font-semibold text-gray-900">Fly.io (Hosting)</h3>
							<p className="text-sm text-gray-600">
								Hosts our website. View their privacy policy at
								fly.io/legal/privacy-policy
							</p>
						</div>
					</div>

					<p className="mt-6 text-gray-700">
						<strong>We do NOT use:</strong> Google Analytics, Facebook Pixel,
						advertising networks, data brokers, or any tracking/surveillance
						tools.
					</p>
				</section>

				{/* 5. Your Rights (GDPR/UK GDPR) */}
				<section className="mb-12">
					<h2 className="mb-6 text-3xl font-bold text-gray-900">
						5. Your Rights
					</h2>
					<p className="mb-4 text-gray-700">Under UK GDPR, you have the right to:</p>
					<ul className="ml-6 space-y-2 text-gray-700">
						<li className="flex items-start">
							<span className="mr-2 text-purple-600">•</span>
							<span>
								<strong>Access:</strong> Request a copy of your data (email us)
							</span>
						</li>
						<li className="flex items-start">
							<span className="mr-2 text-purple-600">•</span>
							<span>
								<strong>Correction:</strong> Update incorrect information
							</span>
						</li>
						<li className="flex items-start">
							<span className="mr-2 text-purple-600">•</span>
							<span>
								<strong>Deletion:</strong> Request deletion of your account and
								data
							</span>
						</li>
						<li className="flex items-start">
							<span className="mr-2 text-purple-600">•</span>
							<span>
								<strong>Portability:</strong> Export your data (built into the app)
							</span>
						</li>
						<li className="flex items-start">
							<span className="mr-2 text-purple-600">•</span>
							<span>
								<strong>Objection:</strong> Object to processing (contact us)
							</span>
						</li>
					</ul>
					<p className="mt-4 text-gray-700">
						To exercise these rights, email{' '}
						<a
							href="mailto:hello@mybillingsmethod.com"
							className="text-purple-600 underline hover:text-purple-700"
						>
							hello@mybillingsmethod.com
						</a>
					</p>
				</section>

				{/* 6. Data Retention */}
				<section className="mb-12">
					<h2 className="mb-6 text-3xl font-bold text-gray-900">
						6. Data Retention
					</h2>
					<ul className="space-y-2 text-gray-700">
						<li className="flex items-start">
							<span className="mr-2 text-purple-600">•</span>
							<span>
								<strong>Account data:</strong> Retained while your account is active
							</span>
						</li>
						<li className="flex items-start">
							<span className="mr-2 text-purple-600">•</span>
							<span>
								<strong>After cancellation:</strong> Deleted within 30 days (unless
								required by law)
							</span>
						</li>
						<li className="flex items-start">
							<span className="mr-2 text-purple-600">•</span>
							<span>
								<strong>Fertility data:</strong> Stays on your Mac until you delete
								it
							</span>
						</li>
					</ul>
				</section>

				{/* 7. Children's Privacy */}
				<section className="mb-12">
					<h2 className="mb-6 text-3xl font-bold text-gray-900">
						7. Children's Privacy
					</h2>
					<p className="text-gray-700">
						MyBillingsMethod is not intended for users under 18. We do not
						knowingly collect information from children. If you believe a child has
						created an account, contact us immediately.
					</p>
				</section>

				{/* 8. Changes to Privacy Policy */}
				<section className="mb-12">
					<h2 className="mb-6 text-3xl font-bold text-gray-900">
						8. Changes to This Policy
					</h2>
					<p className="text-gray-700">
						We may update this privacy policy. We'll notify you of significant
						changes via email. Continued use after changes means you accept the
						updated policy.
					</p>
				</section>

				{/* 9. Contact */}
				<section className="mb-12">
					<h2 className="mb-6 text-3xl font-bold text-gray-900">
						9. Contact Us
					</h2>
					<p className="mb-4 text-gray-700">
						Questions about this privacy policy?
					</p>
					<div className="rounded-lg bg-gray-50 p-6">
						<p className="text-gray-900">
							<strong>Email:</strong>{' '}
							<a
								href="mailto:hello@mybillingsmethod.com"
								className="text-purple-600 underline hover:text-purple-700"
							>
								hello@mybillingsmethod.com
							</a>
						</p>
						<p className="mt-2 text-gray-900">
							<strong>Company:</strong> CHRIDAM LTD
						</p>
						<p className="mt-2 text-gray-900">
							<strong>Location:</strong> United Kingdom
						</p>
					</div>
				</section>
			</div>

			{/* Footer */}
			<footer className="border-t border-gray-200 bg-white py-12">
				<div className="mx-auto max-w-5xl px-6">
					<div className="flex flex-col items-center justify-between gap-4 text-sm text-gray-600 md:flex-row">
						<div className="flex items-center space-x-2">
							<Icon name="lock-closed" className="h-5 w-5 text-purple-600" />
							<span className="font-semibold text-gray-900">
								MyBillingsMethod
							</span>
						</div>
						<div className="flex gap-6">
							<Link to="/privacy" className="hover:text-gray-900">
								Privacy
							</Link>
							<Link to="/tos" className="hover:text-gray-900">
								Terms
							</Link>
							<Link to="/support" className="hover:text-gray-900">
								Support
							</Link>
						</div>
					</div>
					<p className="mt-6 text-center text-xs text-gray-500">
						© {new Date().getFullYear()} MyBillingsMethod. Your data is yours.
						Always local. Always private.
					</p>
				</div>
			</footer>
		</div>
	)
}
